import { Loader2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context.js';
import { Button } from './ui/button.js';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Auth guard for the customer SPA. Renders children when the user has
 * a valid customer JWT in sessionStorage; otherwise shows a "Sign in"
 * button that triggers the popup OAuth flow against Workfront.
 *
 * The licenseId comes from the URL query string — Workfront passes it
 * when mounting the iframe (`?licenseId=<lid>`). Without it we can't
 * start the OAuth handshake (we need to know which Workfront tenant
 * to redirect to).
 *
 * Mirrors the responsibility of `apps/admin-console-spa/src/components/
 * RequireAuth.tsx` (operator side, MSAL Entra) — different IdP, same
 * shape.
 */
export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { session, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const licenseId = searchParams.get('licenseId');

  // Auto-trigger sign-in only once on mount if we have a licenseId
  // but no session AND the URL contains an `auto-signin=1` flag —
  // mostly for tests + dev. Production iframe always requires the
  // user gesture below to satisfy popup blockers.
  useEffect(() => {
    if (!session && licenseId && searchParams.get('auto-signin') === '1' && !signingIn) {
      void handleSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (session) return <>{children}</>;

  const handleSignIn = async (): Promise<void> => {
    if (!licenseId) {
      setError('Missing licenseId in URL — Workfront did not pass tenant context.');
      return;
    }
    setSigningIn(true);
    setError(null);
    try {
      await signIn(licenseId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-gray-900">
      <div className="max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
          Sign in to WorkFocus
        </h1>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          You'll be redirected to Workfront to authenticate. A popup will open — make sure your
          browser allows popups for this site.
        </p>
        {!licenseId ? (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
          >
            Missing license context. Open this app from inside Workfront — the iframe URL must
            include <code className="font-mono">?licenseId=&lt;id&gt;</code>.
          </div>
        ) : (
          <>
            <Button onClick={handleSignIn} disabled={signingIn}>
              {signingIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-label="Signing in" />
                  Signing in…
                </>
              ) : (
                'Sign in with Workfront'
              )}
            </Button>
            {error ? (
              <div
                role="alert"
                className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
